import React, { useState, useEffect, useCallback } from "react";
import LocationDropdownTailwind from "./components/LocationDropdownTailwind";
import MenuList from "./components/MenuList";
import { CartProvider, useCart } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { BillingProvider } from "./context/BillingContext";
import "./components/MenuList.css";
import { initGA, trackMenuView } from "./utils/analytics";

const CAFE_STORAGE_KEY = 'selectedCafe';

function MenuWithCart({ selectedCafe, onClearCafe, showCafeSelector }) {
  const cart = useCart();

  return (
    <MenuList
      selectedCafe={selectedCafe}
      onClearCafe={onClearCafe}
      showCafeSelector={showCafeSelector}
      cart={cart}
    />
  );
}

export default function App() {
  const [selectedCafe, setSelectedCafe] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initGA();
  }, []);

  const updateQueryParam = useCallback((cafeCode) => {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);

    if (cafeCode) {
      url.searchParams.set("cafeCode", cafeCode);
    } else {
      url.searchParams.delete("cafeCode");
    }

    window.history.replaceState({}, "", url.toString());
  }, []);

  useEffect(() => {
    const savedCafe = localStorage.getItem(CAFE_STORAGE_KEY);
    const queryCafeCode = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("cafeCode")
      : null;

    if (queryCafeCode) {
      const cafeCode = queryCafeCode.trim().toUpperCase();

      if (savedCafe) {
        try {
          const parsedCafe = JSON.parse(savedCafe);
          const parsedCafeCode = parsedCafe?.ristaId ?? parsedCafe?.cafeCode;

          if (parsedCafeCode && String(parsedCafeCode) === cafeCode) {
            setSelectedCafe(parsedCafe);
            setShowMenu(true);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error parsing saved cafe:", error);
          localStorage.removeItem(CAFE_STORAGE_KEY);
        }
      }

      setSelectedCafe({ ristaId: cafeCode });
      setShowMenu(true);
      setLoading(false);
      return;
    }

    if (savedCafe) {
      try {
        const parsedCafe = JSON.parse(savedCafe);
        setSelectedCafe(parsedCafe);
        setShowMenu(true);
      } catch (error) {
        console.error("Error parsing saved cafe:", error);
        localStorage.removeItem(CAFE_STORAGE_KEY);
      }
    }

    setLoading(false);
  }, [updateQueryParam]);

  const handleCafeSelect = useCallback((cafe) => {
    if (!cafe) {
      return;
    }

    try {
      localStorage.setItem(CAFE_STORAGE_KEY, JSON.stringify(cafe));
    } catch (error) {
      console.error("Unable to store cafe in localStorage:", error);
    }

    setSelectedCafe(cafe);
    setShowMenu(true);

    // Track menu view after state is set
    setTimeout(() => {
      trackMenuView(
        cafe?.ristaId || cafe?.cafeCode,
        cafe?.name
      );
    }, 100);
  }, []);

  // Optional: Add function to clear cafe selection (if needed later)
  const handleClearCafeSelection = () => {
    localStorage.removeItem(CAFE_STORAGE_KEY);
    setSelectedCafe(null);
    setShowMenu(false);
    updateQueryParam(null);
  };

  // Show loading while checking localStorage
  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold">Loading BTC Cafe...</h2>
        </div>
      </div>
    );
  }

  // Show cafe selection if no cafe is selected
  if (!showMenu) {
    const initialId = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("cafeCode") || ""
      : "";

    return (
      <LocationDropdownTailwind
        onCafeSelect={handleCafeSelect}
        initialSelectedId={initialId}
      />
    );
  }

  // Show menu with selected/saved cafe
  const hasStoreIdInQuery = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).has("cafeCode");

  const activeCafeCode = String(
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("cafeCode")
      : null) || selectedCafe?.ristaId || selectedCafe?.cafeCode || "default"
  ).trim().toUpperCase();

  return (
    <BillingProvider>
      <AuthProvider>
        <CartProvider cafeCode={activeCafeCode}>
          <MenuWithCart
            selectedCafe={selectedCafe}
            onClearCafe={handleClearCafeSelection}
            showCafeSelector={!hasStoreIdInQuery}
          />
        </CartProvider>
      </AuthProvider>
    </BillingProvider>
  );
}
